import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import theme from './theme';
import api, { errorMessage } from './lib/api';
import { ToastProvider, useToast } from './components/ui/ToastProvider';
import AppShell from './components/layout/AppShell';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Devices from './pages/Devices';
import DeviceProfile from './pages/DeviceProfile';
import DeviceData from './pages/DeviceData';
import Alerts from './pages/Alerts';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import SignageManager from './pages/SignageManager';
import Subscription from './pages/Subscription';
import Firmware from './pages/Firmware';
import DeviceAllocation from './pages/DeviceAllocation';
import SubOfficeManagement from './pages/SubOfficeManagement';
import MapSettings from './pages/MapSettings';
import AuditLogs from './pages/AuditLogs';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function ProtectedShell({ user, onLogout }) {
  return (
    <AppShell user={user} onLogout={onLogout}>
      <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/users" element={<AdminOnly user={user}><Users /></AdminOnly>} />
        <Route path="/devices" element={<Devices />} />
        <Route path="/devices/:id" element={<DeviceProfile />} />
        <Route path="/device-data" element={<DeviceData />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<AdminOnly user={user}><Settings /></AdminOnly>} />
        <Route path="/signage" element={<AdminOnly user={user}><SignageManager /></AdminOnly>} />
        <Route path="/firmware" element={<AdminOnly user={user}><Firmware /></AdminOnly>} />
        <Route path="/subscription" element={<AdminOnly user={user}><Subscription /></AdminOnly>} />
        <Route path="/allocation" element={<AdminOnly user={user}><DeviceAllocation /></AdminOnly>} />
        <Route path="/sub-offices" element={<AdminOnly user={user}><SubOfficeManagement /></AdminOnly>} />
        <Route path="/map-settings" element={<AdminOnly user={user}><MapSettings /></AdminOnly>} />
        <Route path="/audit-logs" element={<AdminOnly user={user}><AuditLogs /></AdminOnly>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppShell>
  );
}

function AdminOnly({ user, children }) {
  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function AuthenticatedApp() {
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setAuth({ token: localStorage.getItem('token') });
    } catch (error) {
      localStorage.removeItem('token');
      setAuth(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = useCallback(
    async (token) => {
      localStorage.setItem('token', token);
      setAuth({ token });
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        toast.error(errorMessage(error, 'Unable to load the operator profile.'));
      }
      setLoading(false);
    },
    [toast]
  );

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setAuth(null);
    setUser(null);
    window.location.href = '/login';
  }, []);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: theme.palette.background.default }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={30} thickness={3} />
          <Box sx={{ fontSize: '0.7rem', letterSpacing: '0.24em', color: 'text.secondary' }}>
            INITIALISING COMMAND CENTER
          </Box>
        </Box>
      </Box>
    );
  }

  if (!auth) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={login} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AuthContext.Provider value={{ ...auth, user, setUser, logout }}>
      <ProtectedShell user={user} onLogout={logout} />
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <AuthenticatedApp />
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
